import { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import Auth from "../Auth";
import Utils from "./../Utils";
import CopyToClipboardButton from "./CopyToClipboardButton.tsx";

function Completions(props) {
  const showUserColumn = props.showUserColumn !== undefined ? props.showUserColumn : true;
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setPage] = useState(1);

  const fetchCompletions = useCallback(async (page) => {
    setLoading(true);
    try {
      const data = await Auth.fetchWithIdToken('/api/admin/completions', {
        page,
        limit: 25,
        userId: props.userId
      });

      if (data.success) {
        if (showUserColumn && props.userCache) {
          const userIds = [...new Set(data.completions.map((completion) => completion.userId))];
          await props.userCache.fetchIds(userIds);
        }
        setCompletions(data.completions);
      } else {
        console.error('Error fetching completions:', data.error);
      }
    } catch (error) {
      console.error('Error fetching completions:', error);
    } finally {
      setLoading(false);
    }
  }, [props.userId, props.userCache, showUserColumn]);

  useEffect(() => {
    fetchCompletions(currentPage);
  }, [currentPage, fetchCompletions]);

  const columns = useMemo(() => {
    const cols = [
      {
        name: "ID",
        width: "250px",
        selector: (row) => {
          return row.id;
        },
        sortable: false,
        cell: (row) => (
          <CopyToClipboardButton
            textToCopy={row.id}
            successMessage={`Copied ID to clipboard: ${row.id}`}
            className="font-mono w-full"
          />
        ),
      },
      {
        name: "Date",
        width: "160px",
        selector: (row) => {
          return new Date(row.createdAt).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
        sortable: false,
        cell: (row) => (
          <div className="text-sm text-gray-600">
            {new Date(row.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        ),
      },
      {
        name: "Source",
        width: "265px",
        selector: (row) => {
          return row.source;
        },
        sortable: false,
        cell: (row) => <div className="text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap">{row.source}</div>,
      },
      {
        name: "Engine",
        width: "200px",
        selector: (row) => {
          return row.engine || "davinci";
        },
        sortable: false,
        cell: (row) => <div className="text-xs text-gray-600 font-mono overflow-hidden text-ellipsis whitespace-nowrap">{row.engine || "davinci"}</div>,
      },
      {
        name: "Prompt",
        width: "100px",
        selector: (row) => {
          if (row.prompt?.slice) {
            return row.prompt.slice(0, 50) + "...";
          } else {
            return JSON.stringify(row.prompt).slice(0, 50) + "...";
          }
        },
        cell: (row) => {
          let prompt;
          if (!row.prompt) {
            return <div className="text-gray-500 italic">No prompt</div>;
          }
          if (row.prompt?.slice) {
            prompt = row.prompt.slice(0, 50) + "...";
          } else {
            prompt = JSON.stringify(row.prompt).slice(0, 50) + "...";
          }
          return <div className="text-sm text-gray-800 leading-tight overflow-hidden text-ellipsis whitespace-nowrap">{prompt}</div>;
        },
      },
      {
        name: "SCIM Flagged",
        width: "100px",
        selector: (row) => {
          const flagged = row.scimFlagged;
          if (flagged === undefined) {
            return "Unknown";
          } else {
            return flagged ? "true" : "false";
          }
        },
        sortable: false,
        cell: (row) => {
          const flagged = row.scimFlagged;
          let colorClass = "text-gray-600";
          let text = "Unknown";

          if (flagged === true) {
            colorClass = "text-red-600";
            text = "Yes";
          } else if (flagged === false) {
            colorClass = "text-green-600";
            text = "No";
          }

          return <div className={`text-sm font-medium ${colorClass}`}>{text}</div>;
        },
      },
      {
        name: "SCIM Score",
        width: "130px",
        selector: (row) => {
          return row.scimScore;
        },
        sortable: false,
        cell: (row) => <div className="text-sm text-gray-600 text-center overflow-hidden text-ellipsis whitespace-nowrap">{row.scimScore || "N/A"}</div>,
      },
    ];

    if (showUserColumn) {
      cols.splice(2, 0, {
        name: "User",
        width: "150px",
        selector: (row) => {
          const user = props.userCache?.getUser(row.userId);
          return user ? user.displayName || user.email : null;
        },
        sortable: false,
        cell: (row) => {
          const user = props.userCache?.getUser(row.userId);
          if (user) {
            return (
              <Link
                to={"/admin/user/" + user.email}
                className="text-app-primary hover:underline px-1 py-1 rounded text-s transition-colors"
              >
                {user.displayName || user.email}
              </Link>
            );
          }
          return <span className="text-gray-500 italic">No User</span>;
        },
      });
    }

    return cols;
  }, [props.userCache, showUserColumn]);

  const ExpandableComponent = ({ data }) => {
    let completions = data.completions.map((c, i) => {
      return (
        <div
          key={`completion-${i}`}
          className="mb-4"
        >
          <h4 className="font-semibold text-gray-800 mb-2">#{i + 1}</h4>
          <p className="text-sm text-gray-700 leading-relaxed">{Utils.nl2br(c)}</p>
        </div>
      );
    });

    return (
      <div className="border-l-4 border-blue-500 bg-gray-50 p-6 mx-4 my-2 rounded-r-lg">
        <div className="flex flex-wrap gap-6 mb-4 text-xs text-gray-600">
          {data.invocationId && (
            <div>
              <span className="font-semibold text-gray-700">Invocation:</span>{" "}
              <Link
                to={`/admin/invocation/${data.invocationId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-app-primary hover:underline"
              >
                {data.invocationId}
              </Link>
            </div>
          )}
          {typeof data.credit_cost === "number" && (
            <div>
              <span className="font-semibold text-gray-700">Credits Used:</span> {data.credit_cost.toLocaleString()}
            </div>
          )}
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Prompt</h3>
        {data.prompt && <p className="text-sm text-gray-700 mb-6 leading-relaxed">{Utils.nl2br(data.prompt)}</p>}

        {data.messages && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Messages</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {Utils.nl2br(
                data.messages
                  .map((m) => `${m.role}: ${m.content}`)
                  .join("\n")
              )}
            </p>
          </div>
        )}

        <h3 className="text-lg font-semibold text-gray-800 mb-3">Completions</h3>
        <div className="space-y-4">{completions}</div>
      </div>
    );
  };

  return (
    <div className="p-4">
      <DataTable
        columns={columns}
        responsive={true}
        progressPending={loading}
        expandableRows
        expandableRowsComponent={<ExpandableComponent />}
        pagination
        paginationServer
        paginationPerPage={25}
        paginationRowsPerPageOptions={[25]}
        paginationTotalRows={99999999}
        onChangePage={(page) => {
          setPage(page);
        }}
        data={completions}
      />
    </div>
  );
}

export default Completions;
